import React from "react";
import { ArrowRight, Building2, BrainCircuit, Rocket, Infinity as InfinityIcon } from "lucide-react";
import { JOURNEY_STEPS, DIFFERENCE_CARDS } from "../../data/transform";

const DICONS = { Building2, BrainCircuit, Rocket, Infinity: InfinityIcon };

export const JourneySection = () => {
  const [currentCircle, setCurrentCircle] = React.useState(0);
  const [animStep, setAnimStep] = React.useState(0); // 0 to 6

  React.useEffect(() => {
    let timer1, timer2;
    let stepIndex = 0;
    let isMounted = true;

    const startCycle = () => {
      if (!isMounted) return;
      stepIndex = 0;
      setCurrentCircle(0);
      setAnimStep(0);

      // Circle 1 is filled. After 250ms, start extending line towards Circle 2
      timer1 = setTimeout(() => {
        advanceLine();
      }, 250);
    };

    const advanceLine = () => {
      if (!isMounted) return;
      if (stepIndex < 6) {
        stepIndex += 1;
        // 1. Line extends to next step over 650ms
        setAnimStep(stepIndex);

        // 2. As soon as line reaches the next circle center (650ms), light up that circle!
        timer2 = setTimeout(() => {
          if (!isMounted) return;
          setCurrentCircle(stepIndex);

          // 3. Pause slightly at the circle, then continue line to next
          timer1 = setTimeout(() => {
            advanceLine();
          }, 200);
        }, 650);
      } else {
        // Step 7 reached! All circles & entire connecting line are filled!
        // Hold for 2.2 seconds then reset and loop!
        timer1 = setTimeout(() => {
          startCycle();
        }, 2200);
      }
    };

    startCycle();

    return () => {
      isMounted = false;
      clearTimeout(timer1);
      clearTimeout(timer2);
    };
  }, []);

  return (
    <section
      id="journey"
      className="w-full py-18 lg:py-24"
      style={{ backgroundColor: "rgba(247, 250, 253, 1)" }}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
        {/* Header row matching screenshot */}
        <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
          <div className="flex flex-col gap-2.5">
            <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#51627C]">
              THE SIMPEL AI TRANSFORMATION JOURNEY
            </p>
            <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold tracking-tight text-[#06152B] leading-tight">
              A proven path from insight to impact.
            </h2>
          </div>

          <div className="max-w-[490px] lg:border-l lg:border-[#CBD5E1] lg:pl-8">
            <p className="text-[14.5px] sm:text-[15.5px] leading-[24px] text-[#51627C]">
              We combine business understanding, AI expertise and execution strength to help you move from opportunity to real, measurable outcomes.
            </p>
          </div>
        </div>

        {/* 7-Step Animated Timeline with continuous connecting line */}
        <div className="mt-14 lg:mt-20 overflow-x-auto pb-6 pt-2">
          <div className="min-w-[940px] lg:min-w-0 w-full relative">
            
            {/* Continuous background track line running directly through circle centers */}
            <div
              className="absolute h-[3.5px] sm:h-[4px] bg-[#E2EAF2] rounded-full z-0 pointer-events-none"
              style={{
                left: "calc(100% / 14)", // exact center of first circle
                right: "calc(100% / 14)", // exact center of last circle
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              {/* Animated gradient fill line extending from circle to circle */}
              <div
                className="h-full rounded-full transition-all duration-[650ms] ease-linear"
                style={{
                  width: `${(animStep / 6) * 100}%`,
                  background: "linear-gradient(90deg, #00D2EE 0%, #2F74F5 100%)",
                  boxShadow: animStep > 0 ? "0 0 12px rgba(0, 210, 238, 0.65)" : "none",
                }}
              />
            </div>

            {/* 7 Step Nodes Row */}
            <div className="relative z-10 flex items-center justify-between w-full">
              {JOURNEY_STEPS.map((s, index) => {
                const isEven = index % 2 === 1; // 1, 3, 5 (Steps 2, 4, 6)
                const isCircleActive = currentCircle >= index;
                const circleGradient = "linear-gradient(135deg, #00D2EE 0%, #2F74F5 100%)";

                return (
                  <div
                    key={s.title}
                    className="relative flex flex-col items-center text-center"
                    style={{ width: "calc(100% / 7)" }}
                  >
                    {/* Top Label slot (for even steps: 2, 4, 6) */}
                    <div className="h-[105px] flex flex-col justify-end pb-3.5 w-[130px] sm:w-[155px] xl:w-[170px] px-1">
                      {isEven ? (
                        <div>
                          <h4 className="text-[13.5px] sm:text-[14.5px] font-bold text-[#06152B] leading-tight mb-1">
                            {s.title}
                          </h4>
                          <p className="text-[11px] sm:text-[12px] leading-[15px] text-[#51627C]">
                            {s.sub}
                          </p>
                        </div>
                      ) : null}
                    </div>

                    {/* Circle Node sitting directly on the line */}
                    <div className="relative z-10 my-0.5 flex items-center justify-center">
                      <div
                        className="flex h-[48px] w-[48px] sm:h-[54px] sm:w-[54px] items-center justify-center rounded-full text-[17px] sm:text-[18px] font-bold transition-all duration-300 select-none shadow-md"
                        style={{
                          background: isCircleActive ? circleGradient : "#FFFFFF",
                          color: isCircleActive ? "#FFFFFF" : "#51627C",
                          border: isCircleActive ? "none" : "1px solid #DCE5EF",
                          boxShadow: isCircleActive
                            ? "0 0 20px 3px rgba(0, 210, 238, 0.55), 0 4px 12px rgba(47, 116, 245, 0.35)"
                            : "0 4px 14px rgba(0, 0, 0, 0.06)",
                          transform: isCircleActive ? "scale(1.06)" : "scale(1)",
                        }}
                      >
                        {index + 1}
                      </div>
                    </div>

                    {/* Bottom Label slot (for odd steps: 1, 3, 5, 7) */}
                    <div className="h-[105px] flex flex-col justify-start pt-3.5 w-[130px] sm:w-[155px] xl:w-[170px] px-1">
                      {!isEven ? (
                        <div>
                          <h4 className="text-[13.5px] sm:text-[14.5px] font-bold text-[#06152B] leading-tight mb-1">
                            {s.title}
                          </h4>
                          <p className="text-[11px] sm:text-[12px] leading-[15px] text-[#51627C]">
                            {s.sub}
                          </p>
                        </div>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>

      </div>
    </section>
  );
};

export const DifferenceSection = () => (
  <section className="w-full" style={{ background: "#060D16" }}>
    <div className="mx-auto max-w-[1440px] px-6 py-20 lg:px-[70px] lg:py-24">
      
      {/* Header & Description aligned to match the image */}
      <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
        <div className="flex flex-col gap-3">
          <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#94A3B8]">
            TURNING IDEAS INTO REAL-WORLD IMPACT
          </p>
          <h2 className="text-[36px] font-bold text-white sm:text-[44px]">
            The Simpel difference.
          </h2>
        </div>
        <p className="max-w-[500px] text-[16px] font-normal leading-[26px] text-[#94A3B8] lg:text-[17px]">
          We don’t just build AI solutions. We partner with you to continuously identify,
          deliver and scale value — so your business stays ahead.
        </p>
      </div>

      {/* Cards Grid */}
      <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {DIFFERENCE_CARDS.map((c) => {
          const Icon = DICONS[c.icon] || Building2;
          return (
            <div 
              key={c.title} 
              className="flex flex-col items-center rounded-[16px] border border-[#1E293B] bg-[#16202E] p-8 text-center transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0F172A]/50"
            >
              {/* Scaled up Icon and Glow Effect */}
              <div className="relative mb-5 flex h-[72px] w-[72px] items-center justify-center">
                <span 
                  className="absolute inset-0 rounded-full opacity-30 blur-[14px]" 
                  style={{ background: c.color }} 
                />
                <span className="relative flex h-[48px] w-[48px] items-center justify-center rounded-full bg-white shadow-sm">
                  <Icon className="h-6 w-6" strokeWidth={2.5} style={{ color: c.color }} />
                </span>
              </div>
              
              {/* Scaled up Typography */}
              <h3 className="mb-3 text-[18px] font-semibold text-white">
                {c.title}
              </h3>
              <p className="text-[14px] leading-[22px] text-[#94A3B8]">
                {c.sub}
              </p>
            </div>
          );
        })}
      </div>
      
    </div>
  </section>
);